import * as React from "react";
import { Grid } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import { useContact, useContactFields, useCreateContact, useUpdateContact } from "@/hooks/api/use-contacts";
import { ChipsFreeSolo } from "@/components/chips-free-solo";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { CustomDrawer } from "@/components/custom-drawer";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

export function CreateContact({
	isCreateContactDrawerOpen,
	onCreateDrawerClose,
	contactId,
}: {
	isCreateContactDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
	contactId: string | null;
}): React.JSX.Element {
	const isEdit = !!contactId;

	const { data: contact, isLoading: isContactLoading } = useContact(contactId || "");
	const { data: fields = [], isLoading: isFieldsLoading } = useContactFields();

	const createContactMutation = useCreateContact();
	const updateContactMutation = useUpdateContact();

	const methods = useForm<any>({
		defaultValues: {
			phone: "",
			attributes: {},
			tags: [],
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setError,
		watch,
		setValue,
		formState: { isSubmitting },
	} = methods;

	const tags = watch("tags");

	React.useLayoutEffect(() => {
		if (contact && isEdit) {
			reset({
				phone: contact.phone,
				attributes: contact.attributes,
				tags: contact.tags.map((tag: any) => ({ value: tag.id, label: tag.label })),
			});
		} else {
			reset({
				phone: "",
				attributes: {},
				tags: [],
			});
		}
	}, [contact, isEdit, reset, isCreateContactDrawerOpen]);

	const onSubmitForm = async (values: any) => {
		try {
			const formData = {
				phone: values.phone,
				attributes: values.attributes,
				tags: values.tags.map((tag: any) => tag.label),
			};

			await (isEdit && contactId
				? updateContactMutation.mutateAsync({ id: contactId, data: formData })
				: createContactMutation.mutateAsync(formData));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;

			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	const isLoading = isContactLoading || isFieldsLoading;

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreateContactDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Contact" : "Create Contact"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={isSubmitting || createContactMutation.isPending || updateContactMutation.isPending}
				submitButtonText={isEdit ? "Update Contact" : "Create Contact"}
      	width="420px"
			>
				<ComponentLoader loading={isLoading} />
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 12 }}>
							<DynamicField
								control={control}
								name="phone"
								type={DynamicFieldType.TEXT}
								label="Phone"
								dynamicField={{
									name: "phone",
									label: "Phone",
									required: true,
									type: DynamicFieldType.TEXT,
								}}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<ChipsFreeSolo
								placeholder="Enter tags"
								value={tags}
								setValue={(value) => setValue("tags", value)}
								withOptions
								getOptionsUrl="/crm/tags"
								fetchLabel="label"
								fetchValue="name"
								isTags
								label="Tags"
							/>
						</Grid>
						{fields.map((field: any) => (
							<Grid size={{ xs: 12, md: 12 }} key={field.id}>
								<DynamicField
									control={control}
									name={`attributes.${field.name}`}
									type={field.type}
									label={field.label}
									dynamicField={field.field}
									formName="attributes"
								/>
							</Grid>
						))}
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
